import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { getCoafGroupLabel } from '../../services/complianceTranslations';

const COAF_GROUP_ORDER = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'CIRCULAR_3978',
];

const normalizeCode = (value) => String(value || '').trim().toUpperCase();

const resolveRuleGroup = (baseRule, policyRule) => policyRule?.coafGroup || baseRule?.coafGroup || 'Outros';
const resolveRuleTitle = (baseRule, policyRule) => policyRule?.title || baseRule?.title || '-';

const buildDifferenceLabel = (comparison) => {
  if (!comparison.inBasePlan && comparison.inEffectivePolicy) {
    return 'Somente na policy';
  }

  if (comparison.inBasePlan && !comparison.inEffectivePolicy) {
    return 'Removida na policy';
  }

  if (comparison.baseActive !== comparison.policyActive) {
    return comparison.policyActive ? 'Ativada na policy' : 'Desativada na policy';
  }

  return 'Sem alteração';
};

const buildDifferenceTone = (comparison) => {
  if (!comparison.inBasePlan && comparison.inEffectivePolicy) {
    return 'info';
  }

  if (comparison.inBasePlan && !comparison.inEffectivePolicy) {
    return 'warning';
  }

  if (comparison.baseActive !== comparison.policyActive) {
    return comparison.policyActive ? 'success' : 'warning';
  }

  return 'default';
};

export default function RulesComparisonPreview({
  baseRules = [],
  effectiveRules = [],
  loading = false,
  errorMessage = '',
}) {
  const comparisons = React.useMemo(() => {
    const baseRulesByCode = baseRules.reduce((acc, rule) => {
      const key = normalizeCode(rule.code);
      if (key) {
        acc[key] = rule;
      }
      return acc;
    }, {});

    const effectiveRulesByCode = effectiveRules.reduce((acc, rule) => {
      const key = normalizeCode(rule.code);
      if (key) {
        acc[key] = rule;
      }
      return acc;
    }, {});

    const allCodes = Array.from(new Set([
      ...Object.keys(baseRulesByCode),
      ...Object.keys(effectiveRulesByCode),
    ]));

    return allCodes.map((code) => {
      const baseRule = baseRulesByCode[code] || null;
      const policyRule = effectiveRulesByCode[code] || null;

      return {
        code,
        title: resolveRuleTitle(baseRule, policyRule),
        group: resolveRuleGroup(baseRule, policyRule),
        inBasePlan: Boolean(baseRule),
        inEffectivePolicy: Boolean(policyRule),
        baseActive: Boolean(baseRule?.active),
        policyActive: Boolean(policyRule?.active),
      };
    });
  }, [baseRules, effectiveRules]);

  const changedComparisons = React.useMemo(() => {
    return comparisons.filter((item) => (
      !item.inBasePlan
      || !item.inEffectivePolicy
      || item.baseActive !== item.policyActive
    ));
  }, [comparisons]);

  const comparisonsByGroup = React.useMemo(() => {
    return changedComparisons.reduce((acc, comparison) => {
      const key = comparison.group || 'Outros';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(comparison);
      return acc;
    }, {});
  }, [changedComparisons]);

  const sortedGroupKeys = React.useMemo(() => {
    return Object.keys(comparisonsByGroup).sort((a, b) => {
      const left = COAF_GROUP_ORDER.indexOf(a);
      const right = COAF_GROUP_ORDER.indexOf(b);
      return (left === -1 ? 999 : left) - (right === -1 ? 999 : right);
    });
  }, [comparisonsByGroup]);

  const summary = React.useMemo(() => {
    return changedComparisons.reduce((acc, item) => {
      if (!item.inBasePlan && item.inEffectivePolicy) {
        acc.onlyInPolicy += 1;
      } else if (item.inBasePlan && !item.inEffectivePolicy) {
        acc.removedInPolicy += 1;
      } else if (item.baseActive !== item.policyActive) {
        acc.activeStateChanged += 1;
      }

      return acc;
    }, {
      onlyInPolicy: 0,
      removedInPolicy: 0,
      activeStateChanged: 0,
    });
  }, [changedComparisons]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography component="h2" variant="h6">Comparação: Plano-base x Policy efetiva</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Mostra somente diferenças por grupo e regra para facilitar validação antes de salvar.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip size="small" label={`Diferenças: ${changedComparisons.length}`} />
              <Chip size="small" variant="outlined" color="info" label={`Somente policy: ${summary.onlyInPolicy}`} />
              <Chip size="small" variant="outlined" color="warning" label={`Removidas: ${summary.removedInPolicy}`} />
              <Chip size="small" variant="outlined" color="secondary" label={`Mudança de estado: ${summary.activeStateChanged}`} />
            </Stack>
          </Stack>

          {errorMessage ? <Alert severity="warning">{errorMessage}</Alert> : null}

          {loading ? (
            <Stack sx={{ alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
                Carregando regras efetivas da policy...
              </Typography>
            </Stack>
          ) : null}

          {!loading && !changedComparisons.length ? (
            <Alert severity="success">Nenhuma diferença encontrada entre plano-base e policy efetiva.</Alert>
          ) : null}

          {!loading ? sortedGroupKeys.map((group) => (
            <Accordion key={group} variant="outlined" disableGutters>
              <AccordionSummary>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {getCoafGroupLabel(group)}
                  </Typography>
                  <Chip size="small" label={`${comparisonsByGroup[group].length} diferenças`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small" aria-label={`comparacao de regras do grupo ${group}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Título</TableCell>
                        <TableCell>Plano-base</TableCell>
                        <TableCell>Policy efetiva</TableCell>
                        <TableCell>Diferença</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparisonsByGroup[group].map((comparison) => (
                        <TableRow key={`${group}-${comparison.code}`} hover>
                          <TableCell>{comparison.code}</TableCell>
                          <TableCell>{comparison.title}</TableCell>
                          <TableCell>
                            {comparison.inBasePlan ? (comparison.baseActive ? 'Ativa' : 'Inativa') : '-'}
                          </TableCell>
                          <TableCell>
                            {comparison.inEffectivePolicy ? (comparison.policyActive ? 'Ativa' : 'Inativa') : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={buildDifferenceLabel(comparison)}
                              color={buildDifferenceTone(comparison)}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
